// Short, typeable links that stand in for a longer tagged URL. They exist for places where a
// person has to type the address (a slide, a flyer, a spoken mention), so each one carries its
// own utm_source. That keeps typed visits separate from email clicks and QR scans in GA4.
//
// Keys are lowercase with no trailing slash. Once a short link is printed or sent, never
// rename or remove it: the old address would start landing on the NotFound page.
export const SHORT_LINKS: Record<string, string> = {
  '/medical-seminar':
    '/events?audience=medical&utm_source=short-link&utm_medium=vanity&utm_campaign=medical-seminar',
}

// Matching ignores case and a trailing slash, since these links are typed by hand.
export const resolveShortLink = (pathname: string): string | undefined => {
  const normalised = pathname.toLowerCase().replace(/\/+$/, '')
  return SHORT_LINKS[normalised]
}

// Parameters that arrive on the short link (click IDs such as fbclid or gclid when it is shared
// on social or clicked from an ad) are carried over, as is the hash. The destination's own
// parameters win, and any incoming utm_* is dropped: the short link's tags identify the channel.
export const withIncomingParams = (target: string, search: string, hash: string): string => {
  const url = new URL(target, 'https://short-link.invalid')
  new URLSearchParams(search).forEach((value, key) => {
    if (key.startsWith('utm_') || url.searchParams.has(key)) return
    url.searchParams.append(key, value)
  })
  return url.pathname + url.search + hash
}

// Runs before React renders, NOT as a router <Redirect>. GA sends its page view for whatever
// URL is current when it loads, so a router redirect could first record an untagged hit on
// the short path. Rewriting the URL up front means GA4 only ever sees the tagged address.
// replaceState also keeps the short link out of the back-button history.
export const applyShortLink = (): void => {
  if (typeof window === 'undefined') return
  const { pathname, search, hash } = window.location
  const target = resolveShortLink(pathname)
  if (target) window.history.replaceState(null, '', withIncomingParams(target, search, hash))
}
